import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import path from 'path';
import fs from 'fs';
import os from 'os';
import logger from '../utils/logger.js';
import { processTemplate, readTemplate, writeFile, generateUniqueFilename } from '../utils/file-utils.js';
import { runArtilleryTest, generateArtilleryReport, parseArtilleryResults } from '../utils/artillery-utils.js';

// Create a temporary directory for test files
const TEMP_DIR = path.join(os.tmpdir(), 'artillery-mcp');
if (!fs.existsSync(TEMP_DIR)) {
    fs.mkdirSync(TEMP_DIR, { recursive: true });
}

/**
 * Register all tools with the MCP server
 * @param server MCP server instance
 */
export async function registerTools(server: McpServer): Promise<void> {
    logger.info('Registering Artillery tools');

    /**
     * Generates an Artillery test file based on a selected template and parameters.
     * @tool generate-test
     * @param templateType The type of test template to use (http-get, http-post, complex-flow, custom).
     * @param customTemplate Optional custom YAML template content.
     * @param target The target server URL.
     * @param endpoint Optional endpoint path.
     * @param duration Duration of the test in seconds.
     * @param arrivalRate Number of new virtual users per second.
     * @param peakRate Optional peak arrival rate.
     * @param timeout Request timeout in seconds.
     * @param scenarioName Name of the test scenario.
     * @param payload Optional payload for requests.
     * @param auth Optional authentication string.
     * @param additionalConfig Optional additional configuration.
     */
    server.tool(
        'Generate_Artillery_Test',
        'Generates an Artillery test file based on a selected template and parameters.',
        {
            templateType: z.enum(['http-get', 'http-post', 'complex-flow', 'custom']),
            customTemplate: z.string().optional(),
            target: z.string(),
            endpoint: z.string().optional(),
            duration: z.number().default(60),
            arrivalRate: z.number().default(5),
            peakRate: z.number().optional(),
            timeout: z.number().default(30),
            scenarioName: z.string().default('API Test'),
            payload: z.string().optional(),
            auth: z.string().optional(),
            additionalConfig: z.record(z.any()).optional()
        },
        async (params) => {
            try {
                logger.info(`Generating Artillery test with template: ${params.templateType}`);

                // Determine which template to use
                let templateContent: string;
                if (params.templateType === 'custom' && params.customTemplate) {
                    templateContent = params.customTemplate;
                } else {
                    const templateFile = `${params.templateType}.yml`;
                    templateContent = readTemplate(templateFile);
                }

                // Process template with parameters
                const processedTemplate = processTemplate(templateContent, {
                    target: params.target,
                    endpoint: params.endpoint || '/',
                    duration: params.duration,
                    arrivalRate: params.arrivalRate,
                    peakRate: params.peakRate || params.arrivalRate * 2,
                    timeout: params.timeout,
                    scenarioName: params.scenarioName,
                    payload: params.payload || '{}',
                    auth: params.auth || null,
                    ...params.additionalConfig
                });

                // Generate a unique filename and save the test
                const filename = generateUniqueFilename('artillery-test');
                const filePath = path.join(TEMP_DIR, filename);

                writeFile(filePath, processedTemplate);

                return {
                    content: [
                        {
                            type: 'text',
                            text: `Artillery test generated successfully!\n\nTest file: ${filePath}\n\nTest configuration:\n\`\`\`yaml\n${processedTemplate}\n\`\`\`\n\nYou can run this test using the 'run-test' tool with the file path.`
                        }
                    ]
                };
            } catch (error: any) {
                logger.error(`Error generating Artillery test: ${error.message}`);
                return {
                    content: [
                        {
                            type: 'text',
                            text: `Error generating Artillery test: ${error.message}`
                        }
                    ]
                };
            }
        }
    );

    /**
     * Runs an Artillery test from a given test file and optionally generates an HTML report.
     * @tool run-test
     * @param testFilePath Path to the Artillery test YAML file.
     * @param generateReport Whether to generate an HTML report (default: true).
     */
    server.tool(
        'Run_Artillery_Test',
        'Runs an Artillery test from a given test file',
        {
            testFilePath: z.string(),
            generateReport: z.boolean().default(true)
        },
        async (params) => {
            try {
                logger.info(`Running Artillery test: ${params.testFilePath}`);

                // Check if test file exists
                if (!fs.existsSync(params.testFilePath)) {
                    throw new Error(`Test file not found: ${params.testFilePath}`);
                }

                // Generate a unique filename for the results
                const resultsFilename = generateUniqueFilename('artillery-results', 'json');
                const resultsFilePath = path.join(TEMP_DIR, resultsFilename);

                // Run the test
                const testOutput = await runArtilleryTest(params.testFilePath, resultsFilePath);

                // Parse results
                const parsedResults = await parseArtilleryResults(testOutput);

                let reportInfo = '';

                // Generate HTML report if requested
                if (params.generateReport) {
                    const reportFilename = generateUniqueFilename('artillery-report', 'html');
                    const reportFilePath = path.join(TEMP_DIR, reportFilename);

                    await generateArtilleryReport(resultsFilePath, reportFilePath);

                    reportInfo = `\n\nHTML report generated at: ${reportFilePath}`;
                }

                // Format the parsed results
                const formattedResults = JSON.stringify(parsedResults, null, 2);

                return {
                    content: [
                        {
                            type: 'text',
                            text: `Artillery test completed successfully!\n\nResults file: ${resultsFilePath}${reportInfo}\n\nTest summary:\n\`\`\`\n${testOutput}\n\`\`\`\n\nParsed results:\n\`\`\`json\n${formattedResults}\n\`\`\`\n\nYou can analyze these results with the 'analyze-results' tool.`
                        }
                    ]
                };
            } catch (error: any) {
                logger.error(`Error running Artillery test: ${error.message}`);
                return {
                    content: [
                        {
                            type: 'text',
                            text: `Error running Artillery test: ${error.message}`
                        }
                    ]
                };
            }
        }
    );

    /**
     * Analyzes Artillery test results and suggests improvements.
     * @tool analyze-results
     * @param resultsOutput The raw output or file path of Artillery test results.
     * @param suggestImprovements Whether to suggest improvements (default: true).
     */
    server.tool(
        'Analyze_Artillery_Test_Results',
        'Analyzes Artillery test results and suggests improvements',
        {
            resultsOutput: z.string(),
            suggestImprovements: z.boolean().default(true)
        },
        async (params) => {
            try {
                logger.info('Analyzing Artillery test results');
                const parsedResults = await parseArtilleryResults(params.resultsOutput);

                let analysisText = `# Artillery Test Results Analysis\n\n`;
                analysisText += `## Summary\n\n${parsedResults.summary}\n`;

                // If there are errors, highlight them
                if (parsedResults.errors && Object.keys(parsedResults.errors).length > 0) {
                    analysisText += `\n## Error Analysis\n`;
                    for (const [err, count] of Object.entries(parsedResults.errors)) {
                        analysisText += `- **${err}**: ${count}\n`;
                    }
                    analysisText += `\nAll virtual users failed due to the above errors.`;
                } else {
                    analysisText += `\nNo errors detected.`;
                }

                // Suggestions
                if (params.suggestImprovements) {
                    analysisText += `\n\n## Suggestions\n`;
                    if (parsedResults.errors && Object.keys(parsedResults.errors).length > 0) {
                        analysisText += `- Review the error messages above. For "Invalid URL", check your test scenario URLs (they should start with a slash, e.g., \`/v1/getData\`).\n`;
                        analysisText += `- Ensure the target server is reachable and the endpoints exist.\n`;
                    } else {
                        analysisText += `- No major issues detected. Consider increasing load or duration for further testing.\n`;
                    }
                }

                return {
                    content: [
                        {
                            type: 'text',
                            text: analysisText
                        }
                    ]
                };
            } catch (error: any) {
                logger.error(`Error analyzing Artillery results: ${error.message}`);
                return {
                    content: [
                        {
                            type: 'text',
                            text: `Error analyzing Artillery results: ${error.message}`
                        }
                    ]
                };
            }
        }
    );

    /**
     * Creates a custom Artillery test file with the provided name and content.
     * @tool create-custom-test
     * @param name The name for the custom test file.
     * @param content The YAML content of the custom test.
     */
    server.tool(
        'Create_Custom_Artillery_Test',
        'Creates a custom Artillery test file with the provided name and content',
        {
            name: z.string(),
            content: z.string()
        },
        async (params) => {
            try {
                logger.info(`Creating custom Artillery test: ${params.name}`);

                // Sanitize the filename
                const sanitizedName = params.name.replace(/[^a-z0-9-_]/gi, '-').toLowerCase();
                const filename = `${sanitizedName}.yml`;
                const filePath = path.join(TEMP_DIR, filename);

                // Write the custom test
                writeFile(filePath, params.content);

                return {
                    content: [
                        {
                            type: 'text',
                            text: `Custom Artillery test created successfully!\n\nTest file: ${filePath}\n\nYou can run this test using the 'run-test' tool with the file path.`
                        }
                    ]
                };
            } catch (error: any) {
                logger.error(`Error creating custom Artillery test: ${error.message}`);
                return {
                    content: [
                        {
                            type: 'text',
                            text: `Error creating custom Artillery test: ${error.message}`
                        }
                    ]
                };
            }
        }
    );

    logger.info('Artillery tools registered successfully');
}
